/**
 * Murphy Door Configurator - Shelf Mirror Coordinator
 *
 * PURPOSE: apo_style only. Real MageWorx APO has ONE shelf-type control, not
 * two -- Daniel 2026-08-13: "in apo selecting the shelves counts it for both
 * the top and bottom shelves." Top Section and Bottom Section stay two
 * separate, fully real, independently visible/required categories (Daniel,
 * same day: "the top and bottom section are separate from the cabinets and
 * should still work as such") -- this module just keeps their real
 * selections in sync: whichever side the customer picks, the OTHER side
 * auto-applies the SAME-TITLED variant a moment later, through the existing
 * bulk-apply funnel. Bidirectional -- "one card affect the other no matter
 * what," not just top-drives-bottom.
 *
 * An earlier version of this feature (same day) hid the Bottom card
 * entirely and relied on the mirror alone to satisfy completion -- reverted
 * after it broke Add to Cart in a way that wasn't reproducible in isolation.
 * Keeping both cards real/visible/independently-walkable is the simpler,
 * lower-risk shape; this module ONLY auto-applies a selection, it never
 * touches DOM visibility or the apo_style ordering/auto-nav walk.
 *
 * CONSTANTS.APO_STYLE_SHELF_MIRROR_PAIRS: Object.freeze([['Top Section', 'Bottom Section']]).
 * See its doc comment in md-config-core-constants.js for the live-verified
 * title-overlap data this relies on.
 *
 * PHASE: 2 (Event-Driven CCP - Strict Enforcement)
 * MODULE TYPE: Business Logic (bl-)
 * GOLDEN STANDARD REFERENCE: md-config-bl-gate-card-coordinator.js
 *
 * CCP COMPLIANCE:
 * - Event-driven coordination ONLY (no direct BL/cart-manager/price-calculator calls)
 * - PPD-First data access via conductor delegation (getVirtualizedProductsForCategory,
 *   getAppliedSelectionsMap)
 * - Publishes business:cascade_apply_gate_defaults -- consumed by the EXISTING,
 *   unmodified handleSimplifiedDefaultsRequest() bulk-apply funnel in
 *   md-config-bl-cascade-auto-selection-coordinator.js (composition, not duplication)
 * - Bootstrap barrier pattern for race protection (Golden Pattern #9)
 *
 * DATA FLOW:
 * selection:applied (categoryId is in an APO_STYLE_SHELF_MIRROR_PAIRS pair, apo_style active)
 *   -> resolve the applied selection's {productKey, variantTitle} descriptor from
 *      the source category's own product data. BOTH halves matter: the variant
 *      title carries only the material and is shared by every product in the
 *      category, so product identity is what distinguishes Standard from Lipped
 *      (see normalizeShelfProductKey below for the live data behind this)
 *   -> if the OTHER side of the pair already shows that same descriptor applied, stop
 *      (this is what breaks the bidirectional ping-pong: source applies -> target
 *      mirrors -> target's own SELECTION_APPLIED re-enters this handler -> its
 *      "source" is now the original source, which already matches -> no-op)
 *   -> resolveVariantByDescriptor(otherSide, descriptor) -> business:cascade_apply_gate_defaults
 *   -> (existing funnel) -> APPLY_SELECTION on the other side
 */
(function (window) {
  'use strict';

  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;

  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION
  // ============================================================================
  let bootstrapComplete = false;
  let pendingCalls = [];

  // PATTERN 15: CLEANUP ARRAY
  let cleanupHandlers = [];

  /**
   * Collapses a shelf product title to a side-independent identity key.
   *
   * THE BUG THIS EXISTS TO FIX (confirmed live 2026-08-17 against
   * flush-mount-bookcase-door-2-0's PPD): a shelf variant's .title is only
   * the MATERIAL -- "Paint Grade / Unfinished", "Maple Veneer / Medium
   * Stain" -- and all 28 of them appear on BOTH the Standard product and
   * the Lipped product inside the SAME category. Standard-vs-Lipped lives
   * on the product, never on the variant. So matching a mirror target by
   * variant title alone is ambiguous by construction, and the old code took
   * the first product that matched, which is always "Standard ... Shelves"
   * because it sorts first. Picking Lipped on Top therefore applied
   * STANDARD to Bottom -- silently, with the UI showing Lipped.
   *
   * The paired categories do not use identical product titles either
   * ("Lipped Shelves" on Top vs "Lipped Bottom Shelves" on Bottom), so the
   * key drops the side words and compares what is left.
   * @param {string} title
   * @returns {string}
   */
  function normalizeShelfProductKey(title) {
    if (!title) return '';
    return String(title)
      .toLowerCase()
      .replace(/\b(top|bottom|upper|lower)\b/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Resolves the {productKey, variantTitle} pair identifying an applied
   * selection. Both halves are required to mirror correctly -- see
   * normalizeShelfProductKey above for why the variant title alone is not
   * enough. Returns null if the variant isn't found.
   * @param {Object} conductor
   * @param {string} categoryName
   * @param {string|number} variantId
   * @returns {{productKey: string, variantTitle: string}|null}
   */
  function resolveSelectionDescriptor(conductor, categoryName, variantId) {
    if (!conductor || variantId == null) return null;
    try {
      const products =
        conductor.getVirtualizedProductsForCategory(categoryName) || [];
      for (const product of products) {
        const variant = (product.variants || []).find(
          (v) => v && String(v.id) === String(variantId)
        );
        if (variant) {
          return {
            productKey: normalizeShelfProductKey(product.title),
            variantTitle: variant.title,
          };
        }
      }
      return null;
    } catch (error) {
      isDev() &&
        console.error(
          '[ShelfMirrorCoordinator] resolveSelectionDescriptor failed:',
          error
        );
      return null;
    }
  }

  /**
   * Resolves a category's CURRENTLY applied selection's variant title, or
   * null if nothing is applied yet. Used only for the ping-pong guard below.
   * @param {Object} conductor
   * @param {string} categoryName
   * @returns {string|null}
   */
  function resolveCurrentlyAppliedDescriptor(conductor, categoryName) {
    if (!conductor || !conductor.getAppliedSelectionsMap) return null;
    try {
      const appliedMap = conductor.getAppliedSelectionsMap();
      const applied = appliedMap ? appliedMap.get(categoryName) : null;
      if (!applied || applied.variantId == null) return null;
      return resolveSelectionDescriptor(
        conductor,
        categoryName,
        applied.variantId
      );
    } catch (error) {
      isDev() &&
        console.error(
          '[ShelfMirrorCoordinator] resolveCurrentlyAppliedDescriptor failed:',
          error
        );
      return null;
    }
  }

  /**
   * Resolves a category's single composite product's variant by TITLE.
   * Duplicated from md-config-bl-gate-card-coordinator.js's helper of the
   * same name/shape -- CCP forbids direct module-to-module calls (Pattern
   * #8), so a small shared utility like this is duplicated per-consumer
   * rather than imported.
   * @param {Object} conductor
   * @param {string} categoryName
   * @param {string} variantTitle
   * @returns {{productId: string, variantId: string}|null}
   */
  function resolveVariantByDescriptor(conductor, categoryName, descriptor) {
    if (!conductor || !descriptor || !descriptor.variantTitle) return null;
    try {
      const products =
        conductor.getVirtualizedProductsForCategory(categoryName) || [];

      // Collect ALL candidates rather than returning the first hit.
      // Product identity is checked FIRST -- skipping it is the original bug:
      // every product in the category carries the same material variant
      // titles, so a bare `.find` on variant title always won with whatever
      // sorted first, and the mirror silently applied Standard when the
      // customer chose Lipped.
      const matches = [];
      for (const product of products) {
        if (normalizeShelfProductKey(product.title) !== descriptor.productKey)
          continue;
        const variant = (product.variants || []).find(
          (v) => v && v.title === descriptor.variantTitle
        );
        if (variant)
          matches.push({ productId: product.id, variantId: variant.id });
      }

      if (matches.length === 1) return matches[0];

      // More than one product normalized to the same key. Picking one would
      // reintroduce exactly the silent-wrong-answer failure this resolver was
      // written to close, just narrowed to a rarer catalog shape (e.g. both
      // "Lipped Top Shelves" and "Lipped Bottom Shelves" living in the SAME
      // category, since the key strips top/bottom/upper/lower). Refuse and
      // leave the other side unset -- an unanswered card the customer can see
      // and fix beats a wrong shelf type reaching the cart.
      if (matches.length > 1) {
        isDev() &&
          console.warn(
            '[ShelfMirrorCoordinator] Ambiguous mirror target in "' +
              categoryName +
              '": ' +
              matches.length +
              ' products normalize to productKey "' +
              descriptor.productKey +
              '". Refusing to guess -- leaving that side unset.'
          );
        return null;
      }
      // Deliberate no-op rather than a fallback. If the paired category has
      // no product with this identity (an asymmetric catalog, or a renamed
      // product), mirroring ANY other product would be a wrong answer that
      // reaches the cart. Leaving the other side unset is visible to the
      // customer -- an unanswered card they can fill in themselves -- and is
      // the only honest recovery here. Never "best effort" this.
      isDev() &&
        console.warn(
          '[ShelfMirrorCoordinator] No mirror target in "' +
            categoryName +
            '" for productKey "' +
            descriptor.productKey +
            '" / variant "' +
            descriptor.variantTitle +
            '" -- leaving that side unset rather than applying a mismatch.'
        );
      return null;
    } catch (error) {
      isDev() &&
        console.error(
          '[ShelfMirrorCoordinator] resolveVariantByDescriptor failed:',
          error
        );
      return null;
    }
  }

  /**
   * Handles SELECTION_APPLIED. No-ops unless apo_style is active and
   * eventData.categoryId belongs to a shelf-mirror pair -- every other
   * category's real selection passes straight through untouched.
   * @param {Object} eventData - {categoryId, productId, variantId, ...}
   */
  function handleSelectionApplied(eventData) {
    if (!bootstrapComplete) {
      pendingCalls.push({ fn: handleSelectionApplied, args: [eventData] });
      return;
    }
    if (!eventData || !eventData.categoryId) return;

    const pairs = CONSTANTS.APO_STYLE_SHELF_MIRROR_PAIRS || [];
    const pair = pairs.find((p) => p.indexOf(eventData.categoryId) !== -1);
    if (!pair) return; // not part of a mirror pair, nothing to do
    const otherSide = pair.find((c) => c !== eventData.categoryId);
    if (!otherSide) return;

    const conductor = window.MurphyDoorConfigurator.conductor;
    if (!conductor || !conductor._getConfiguratorExperience) return;
    if (conductor._getConfiguratorExperience() !== 'apo_style') return;

    const applied = resolveSelectionDescriptor(
      conductor,
      eventData.categoryId,
      eventData.variantId
    );
    if (!applied) return;

    // Ping-pong guard: if the other side already shows this exact selection
    // applied, either this IS the mirror echo of an apply we just made, or
    // the customer independently picked matching values on both sides --
    // either way, nothing left to do. Without this, a bidirectional pair
    // would apply back and forth forever.
    //
    // BOTH halves must match. Comparing variant title alone (the original
    // code) made this guard fire on a mere MATERIAL match, so with Bottom
    // already on Standard/"Paint Grade / Unfinished", choosing Lipped/"Paint
    // Grade / Unfinished" on Top looked identical to the guard and it
    // returned early -- the mirror did nothing at all and the two sides sat
    // on different shelf types with no error anywhere.
    const otherSideCurrent = resolveCurrentlyAppliedDescriptor(
      conductor,
      otherSide
    );
    if (
      otherSideCurrent &&
      otherSideCurrent.productKey === applied.productKey &&
      otherSideCurrent.variantTitle === applied.variantTitle
    ) {
      return;
    }

    const resolved = resolveVariantByDescriptor(conductor, otherSide, applied);
    if (!resolved) return;

    if (CoreEventBus) {
      CoreEventBus.publish(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_APPLY_GATE_DEFAULTS,
        {
          selections: [
            {
              categoryId: otherSide,
              productId: resolved.productId,
              variantId: resolved.variantId,
            },
          ],
          context: 'shelf_mirror_' + eventData.categoryId,
          timestamp: Date.now(),
          source: 'ShelfMirrorCoordinator',
        }
      );
    }
  }

  // ============================================================================
  // BOOTSTRAP SUBSCRIPTION (MODULE LOAD TIME - RACE PROTECTION, Golden Pattern #9)
  // ============================================================================
  if (CoreEventBus) {
    const unsubBootstrap = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        bootstrapComplete = true;
        if (pendingCalls.length > 0) {
          isDev() &&
            console.log(
              '[ShelfMirrorCoordinator] Processing ' +
                pendingCalls.length +
                ' queued calls'
            );
          pendingCalls.forEach((call) => {
            try {
              call.fn.apply(null, call.args);
            } catch (error) {
              isDev() &&
                console.error(
                  '[ShelfMirrorCoordinator] Error processing queued call:',
                  error
                );
            }
          });
          pendingCalls = [];
        }
      }
    );
    cleanupHandlers.push(unsubBootstrap);

    const unsubSelectionApplied = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SELECTION_APPLIED,
      handleSelectionApplied
    );
    cleanupHandlers.push(unsubSelectionApplied);
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================
  window.MurphyDoorConfigurator.shelfMirrorCoordinator = {
    // Exposed for external reference/testing -- not part of the event-driven
    // public contract, callers should publish SELECTION_APPLIED instead.
    handleSelectionApplied: handleSelectionApplied,
    cleanup: function () {
      cleanupHandlers.forEach((unsub) => {
        try {
          if (typeof unsub === 'function') unsub();
        } catch (error) {
          isDev() &&
            console.error('[ShelfMirrorCoordinator] Cleanup error:', error);
        }
      });
      cleanupHandlers = [];
    },
    getState: function () {
      return Object.freeze({
        bootstrapComplete: bootstrapComplete,
        pendingCallCount: pendingCalls.length,
        subscriptionCount: cleanupHandlers.length,
      });
    },
  };

  isDev() && console.log('[ShelfMirrorCoordinator] Module loaded');
})(window);
